import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons';
import {
  BoxV2 as Box,
  ButtonV2 as Button,
  Drawer,
  FilterType,
  Flex,
  Table,
  TableFilterV2 as TableFilter,
  useTable,
  useFilters,
  ExpandableText,
} from 'portal-commons';

import { eventController, enumController, EventHistory } from '@/apis/core';
import { getBrandNameAutocompleteOptions } from '@/apis/services';
import BrandDetails from '../../Brands/containers/BrandDetails/BrandDetailsV2';
import { PageContainer, PageTitleV2 as PageTitle } from '@/components';
import { Loader } from '@/shared_elements';
import { MAX_EVENT_RECORDS } from '../constants';
import { DEFAULT_ADDITIONAL_FILTERS } from '@/utils/types';
import { toastFlashMessage } from '@/utils';
import { generateAndDownloadFile } from '@/utils/download';
import { convertTimeWithTimezone, processDateRangeQuery } from '@/utils/time';
import { DateRangePresets } from '@/constants/options';
import {
  CLEAR_LOADING_MESSAGE,
  SET_LOADING_MESSAGE,
} from '@/shared_elements/actions';

const FilterConfigs = {
  brandUid: {
    type: FilterType.Search,
    label: 'Brand ID',
    placeholder: 'Enter Brand ID',
  },
  brandName: {
    type: FilterType.Search,
    label: 'Brand DBA Name',
    placeholder: 'Enter Brand DBA Name',
    suggestions: [],
  },
  eventType: {
    type: FilterType.Dropdown,
    label: 'Event Type',
    placeholder: 'Select Event Type',
    options: [],
  },
  dateRange: {
    type: FilterType.DateRange,
    label: 'Date Range',
    placeholder: 'Select Date Range',
    delayUpdate: false,
    dateRangePresets: DateRangePresets,
  },
};

const FilterLoaders = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (value: string) => getBrandNameAutocompleteOptions(value),
    },
  },
};

const TableHeaders = [
  { id: 'eventType', label: 'EVENT', sortable: true },
  { id: 'categoryType', label: 'CATEGORY', sortable: true },
  { id: 'brandUid', label: 'BRAND ID', sortable: true },
  { id: 'createDate', label: 'DATE & TIME', sortable: true },
  { id: 'description', label: 'DESCRIPTION', sortable: false },
];

const EventsListDE: React.FC = () => {
  const dispatch = useDispatch();
  const [viewingBrand, setViewingBrand] = useState<string>();

  const {
    configs: filterConfigs,
    appliedFilters,
    candidateFilters,
    additionalFilters,
    setOptions,
    handleEdit,
    handleApply,
  } = useFilters({
    configs: FilterConfigs,
    loaders: FilterLoaders,
  });

  const getQuery = (tableFilters: Record<string, unknown> = {}) => {
    const query = {
      ...tableFilters,
      ...appliedFilters,
      ...additionalFilters,
    };
    processDateRangeQuery(query, 'dateRange', 'startDate', 'endDate');
    return query;
  };

  const {
    pageLoading,
    loading: tableLoading,
    filter: tableFilters,
    tableData: events,
    pagination,
    total,
    handleSorting: handleSortingChange,
    handleChangePage: handlePageChange,
  } = useTable<EventHistory>(
    async (tableFilters) => {
      const query = getQuery(tableFilters);
      const response = await eventController.getEventHistory({
        ...query,
      });

      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        rowsPerPage: response?.recordsPerPage || 10,
        page: response?.page || 1,
      };
    },
    {
      cappingTotal: MAX_EVENT_RECORDS,
      deps: [appliedFilters, additionalFilters],
      defaultFilter: DEFAULT_ADDITIONAL_FILTERS,
    }
  );

  const fetchFilterOptions = async () => {
    const eventTypes = await enumController.getEventTypes();
    setOptions(
      'eventType',
      eventTypes.map((item) => ({
        label: item.id,
        value: item.id,
      }))
    );
  };

  const handleDownload = async () => {
    dispatch({
      type: SET_LOADING_MESSAGE,
      payload: 'We are generating the document',
    });
    const query = getQuery(tableFilters);
    const { page, ...restQuery } = query;
    const response = await eventController.downloadEventHistoryAsCsv({
      ...restQuery,
    });
    if (response) {
      const file = new File([response], 'event-history.csv');
      generateAndDownloadFile(file);
    } else {
      toastFlashMessage('Failed to download event history', 'error');
    }
    dispatch({
      type: CLEAR_LOADING_MESSAGE,
    });
  };

  useEffect(() => {
    document.title = 'The Campaign Registry - Events';
    fetchFilterOptions();
  }, []);

  const tableData = useMemo(() => {
    return events.map((event) => ({
      eventType: <span data-testid="eventType">{event.eventType || '--'}</span>,
      categoryType: (
        <span data-testid="categoryType">{event.categoryType || '--'}</span>
      ),
      brandUid: event.brandUid ? (
        <Box
          data-testid="brandUid"
          onClick={async (e) => {
            e.stopPropagation();
            setViewingBrand(event.brandUid);
          }}
          sx={{
            fontWeight: 'normal',
            '&:hover': {
              fontWeight: 'bold',
              cursor: 'pointer',
            },
          }}
        >
          {event.brandUid}
        </Box>
      ) : (
        <span data-testid="brandUid">--</span>
      ),
      createDate: (
        <div data-testid="createDate" style={{ minWidth: 140 }}>
          {convertTimeWithTimezone(event.createDate)}
        </div>
      ),
      description: (
        <Box
          onClick={(e) => e.stopPropagation()}
          data-testid="description"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(200px, 1fr)',
            width: '100%',
          }}
        >
          <ExpandableText text={event.description} maxWidth="100%" />
        </Box>
      ),
    }));
  }, [events]);

  if (pageLoading) return <Loader hocFlag />;

  return (
    <PageContainer
      sx={{
        paddingX: '96px',
        rowGap: null,
        '& .MuiAutocomplete-root': {
          width: 'unset',
        },
        '& [data-testid="eventsTable"]': {
          'td:has([data-testid="description"])': {
            width: '100%',
          },
        },
      }}
      data-testid="eventListsDE"
    >
      <Flex
        sx={{
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <PageTitle
          note={`This page can only show the latest ${MAX_EVENT_RECORDS.toLocaleString(
            'en-US'
          )} records. Contact support for details on your remaining records.`}
        >
          {total} Events
        </PageTitle>
        <Button
          size="lg"
          shape="square"
          variant="outline"
          color="primary"
          startAdornment={faArrowDownToLine}
          onClick={handleDownload}
          data-testid="downloadEventsButton"
          disabled={!total}
        >
          Download
        </Button>
      </Flex>

      <Box sx={{ width: '100%', mb: 'm', mt: 'm' }}>
        <TableFilter
          configs={filterConfigs}
          candidateValues={candidateFilters}
          appliedValues={appliedFilters}
          onCandidateValuesChange={handleEdit}
          onAppliedValuesChange={handleApply}
          data-testid="eventsTableFilters"
        />
      </Box>

      <Table
        loading={tableLoading}
        headRows={TableHeaders}
        emptyState="no events to view"
        data={tableData}
        rowKey="id"
        handleChangePage={handlePageChange}
        createSortHandler={handleSortingChange}
        filter={tableFilters}
        pagination={pagination}
        emptyBlockHeight="180px"
        testId="eventsTable"
      />

      <Drawer
        open={!!viewingBrand}
        initialScreenId="brandDetails"
        onClose={() => setViewingBrand(undefined)}
        width="70%"
      >
        <BrandDetails
          brandUid={viewingBrand}
          onDeleteSuccess={() => {
            setViewingBrand(undefined);
          }}
        />
      </Drawer>
    </PageContainer>
  );
};

export default EventsListDE;
